import { z } from 'zod';
import { roleSchema } from '../schemas/role';

// ── createContact ──

/**
 * Creates a contact — organizer-managed staging identity for someone who
 * hasn't authenticated yet. Global scope, not tied to any event.
 *
 * The contact's `role` field determines their global role when they activate
 * their account. Setting admin/organizer requires the caller to be an admin.
 */
export const createContactInputSchema = z
  .object({
    email: z.string().min(1),
    /** Global role. Defaults to 'invitee'. Setting admin/organizer requires admin caller. */
    role: roleSchema.optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    honorificPrefix: z.string().nullable().optional(),
    honorificSuffix: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    organization: z.string().nullable().optional(),
    workCity: z.string().nullable().optional(),
    workState: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    linkedIn: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    dietaryRestrictions: z.array(z.string()).optional(),
    accessibilityNeeds: z.string().nullable().optional(),
    internalNotes: z.string().nullable().optional(),
    /** Organizer-only categorization tags (e.g. "vip", "speaker"). */
    internalTags: z.array(z.string()).optional(),
    /** HTTPS URL to a profile photo. Optional. */
    photoURL: z.string().nullable().optional(),
    /** Audience IDs the contact should be added to on first activation (ADR-120). */
    stagedAudienceIds: z.array(z.string()).optional(),
    /**
     * HubSpot link established at creation via the organizer typeahead
     * (ADR-169 Stage 2). Both land on `personInternals`; the CRD is
     * captured alongside the link per the Stage 2 spec.
     */
    hubspotContactId: z.string().nullable().optional(),
    hubspotRepcrd: z.string().nullable().optional(),
  })
  .strict();

export type CreateContactInput = z.infer<typeof createContactInputSchema>;

export interface CreateContactResponse {
  success: true;
  contactId: string;
  /** True if a contact already existed for this email (returned existing ID). */
  alreadyExists?: boolean;
}

// ── updateContact ──

/**
 * Overwrite profile/internal fields on an existing person record. Used by
 * the resolve-and-propose user import dialog when the organizer toggles a
 * matched row to 'overwrite'. Server-managed fields (authUid, claimedAt,
 * publicEmail/LinkedIn/etc., role) are deliberately not writeable here —
 * role transitions go through setUserRole instead.
 *
 * Per-field nullability:
 *   - undefined: leave existing value untouched
 *   - null: clear the field
 *   - string / array: overwrite
 *
 * The callable touches `persons` (firstName / lastName / honorifics /
 * photoURL / displayName), `personProfiles` (everything except server-
 * managed), and `personInternals` (notes / tags), each only when the
 * relevant field set is non-empty.
 */
export const updateContactInputSchema = z
  .object({
    personId: z.string().min(1),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    honorificPrefix: z.string().nullable().optional(),
    honorificSuffix: z.string().nullable().optional(),
    photoURL: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    organization: z.string().nullable().optional(),
    workCity: z.string().nullable().optional(),
    workState: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    linkedIn: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    dietaryRestrictions: z.array(z.string()).optional(),
    accessibilityNeeds: z.string().nullable().optional(),
    internalNotes: z.string().nullable().optional(),
    internalTags: z.array(z.string()).optional(),
    /**
     * Specific fields to overwrite. Anything not in this array is treated
     * as untouched even if a value is provided. This lets the dialog
     * surface a per-row diff and the caller pick exactly which fields go
     * over.
     */
    fieldsToOverwrite: z.array(z.string()).min(1),
  })
  .strict();

export type UpdateContactInput = z.infer<typeof updateContactInputSchema>;

export interface UpdateContactResponse {
  success: true;
  personId: string;
  /** Fields actually written (subset of fieldsToOverwrite that had non-undefined values). */
  fieldsUpdated: string[];
}

// ── inviteToEvent ──

/**
 * Invites someone to an event. Handles two branches:
 *
 * 1. Email matches an existing authenticated user → create claimed invitation
 *    + immediate membership (role from user's global role), send "you've been
 *    added" email
 * 2. Email matches an existing contact → create pending invitation with code,
 *    send invite email
 *
 * If the email matches neither, returns an error — the caller should use
 * createContact first to stage the identity, then call inviteToEvent.
 *
 * Role is not part of the input. For existing users, the membership role
 * mirrors their global role. For contacts, the role lives on the contact doc.
 */
export const inviteToEventInputSchema = z
  .object({
    email: z.string().min(1),
    eventId: z.string().min(1),
  })
  .strict();

export type InviteToEventInput = z.infer<typeof inviteToEventInputSchema>;

export interface InviteToEventResponse {
  success: true;
  /** Set when an invitation was created. */
  invitationId?: string;
  /** Set when an eventMembership was created (returning user added to event). */
  membershipId?: string;
  /** True if the user was already an active member of the event. */
  alreadyMember?: boolean;
}

// ── bulkInviteToEvent ──

/**
 * Bulk variant of `inviteToEvent`. Server-side single callable that classifies
 * each email, batched-fetches all needed Firestore + Auth state in parallel,
 * and writes invitations + memberships via Firestore `BulkWriter` (auto-
 * throttled, auto-retried).
 *
 * Demo-scale tight path: 500-row Excel uploads complete in seconds rather
 * than the per-row callable's tens of seconds. Cascade triggers on the
 * resulting membership writes use `arrayUnion` field transforms (per the
 * Stage 2G concurrency refactor), so the bulk fan-out is concurrent-safe
 * on the shared `all-members` group.
 */
export const bulkInviteToEventInputSchema = z
  .object({
    eventId: z.string().min(1),
    /** Emails to invite. Normalized + deduped server-side. Capped at 1000. */
    emails: z.array(z.string().min(1)).min(1).max(1000),
  })
  .strict();

export type BulkInviteToEventInput = z.infer<typeof bulkInviteToEventInputSchema>;

export type BulkInviteToEventStatus =
  | 'invited' // invitation created (pending or claimed)
  | 'already_member' // user is already an active member
  | 'already_invited' // pending eventParticipants row with a live invitation (ADR-193: the row decides, not the email)
  | 'not_found' // email matches neither user nor contact — caller skipped
  | 'forbidden' // role-hierarchy guard rejected (target role > caller role)
  | 'failed'; // unexpected error during the per-row write

export interface BulkInviteToEventRowResult {
  email: string;
  status: BulkInviteToEventStatus;
  invitationId?: string;
  /** Populated for the existing-user branch when a membership was written. */
  membershipId?: string;
  /** Populated when status === 'failed' or 'forbidden'. */
  error?: string;
}

export interface BulkInviteToEventResponse {
  success: true;
  results: BulkInviteToEventRowResult[];
  summary: {
    invited: number;
    alreadyMember: number;
    alreadyInvited: number;
    notFound: number;
    forbidden: number;
    failed: number;
  };
}
